"use client";

import { SiteNav, SiteFooter, SUPPORT_STYLES } from "./chrome";
import { STYLES as BASE_STYLES } from "./LandingV2";

const REGISTER_URL = "https://dashboard.lvlup-app.com/auth/register";

const topics = [
  { num: "01", title: "Getting Started", desc: "Setting up your coaching platform, creating your first programs, and launching your branded app." },
  { num: "02", title: "App Customization", desc: "Branding your mobile app, customizing colors, uploading logos, and personalizing the user experience." },
  { num: "03", title: "Client Management", desc: "Adding clients, managing subscriptions, tracking progress, and communication tools." },
  { num: "04", title: "Payment & Billing", desc: "Setting up payment processing, managing subscriptions, and understanding commission structures." },
  { num: "05", title: "Technical Issues", desc: "Troubleshooting app problems, sync issues, and platform performance questions." },
];

const perf = [
  { v: "< 24h", l: "Average Response Time" },
  { v: "98%", l: "Customer Satisfaction" },
  { v: "24/7", l: "Emergency Support" },
  { v: "1,000+", l: "Coaches Supported" },
];

export default function SupportV2() {
  return (
    <div className="lvlup-landing">
      <style dangerouslySetInnerHTML={{ __html: BASE_STYLES + SUPPORT_STYLES }} />
      <SiteNav current="support" />

      <main>

      {/* HERO */}
      <section className="sp-hero">
        <div className="sp-hero-grid" />
        <div className="sp-hero-glow" />
        <div className="lp-container sp-hero-inner">
          <span className="lp-eyebrow">
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <circle cx="6" cy="6" r="4.5" /><path d="M5 5.2A1 1 0 117 5.2c0 1-1 1-1 1.6M6 8.5h.01" />
            </svg>
            Support Center
          </span>
          <h1>We're here to <span className="lp-accent">help you succeed</span></h1>
          <p className="sp-lead">
            Get the support you need to maximize your coaching platform. From technical assistance to
            best practices, our team is dedicated to your success.
          </p>
          <div className="sp-hero-ctas">
            <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" className="lp-btn lp-btn-primary lp-btn-lg">
              Access Your Dashboard
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 7h8m-3-3l3 3-3 3" /></svg>
            </a>
            <a href="mailto:support@lvlup-app.com" className="lp-btn lp-btn-ghost lp-btn-lg">Get Support</a>
          </div>

          <div className="sp-trio">
            <div className="sp-trio-cell">
              <div className="sp-trio-icon">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="7" /><circle cx="10" cy="10" r="3.5" /><circle cx="10" cy="10" r="1" fill="currentColor" /></svg>
              </div>
              <h3>Expert Support</h3>
              <p>Real humans who know the platform — and the coaching trade.</p>
            </div>
            <div className="sp-trio-cell">
              <div className="sp-trio-icon">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 2L4 11h5l-1 7 7-9h-5l1-7z" /></svg>
              </div>
              <h3>Fast Response</h3>
              <p>Replies within 24 hours, every weekday — no autoresponder runaround.</p>
            </div>
            <div className="sp-trio-cell">
              <div className="sp-trio-icon">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5l7-3 7 3v6c0 3-3 5-7 6-4-1-7-3-7-6V5zM7 9l2 2 4-4" /></svg>
              </div>
              <h3>Comprehensive Resources</h3>
              <p>Guides, tutorials, and a knowledge base built specifically for coaches.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SUPPORT BLOCK */}
      <section>
        <div className="lp-container">
          <div className="sp-grid">
            <div className="sp-card">
              <h3>Support Contact</h3>
              <div className="sp-row">
                <div className="sp-row-icon">
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="14" height="11" rx="2" /><path d="M3 7l7 5 7-5" /></svg>
                </div>
                <div>
                  <div className="sp-label">Technical Support</div>
                  <div>
                    <a href="mailto:support@lvlup-app.com" className="sp-strong">support@lvlup-app.com</a>
                    <span className="sp-meta">For platform issues, bugs, and technical questions</span>
                  </div>
                </div>
              </div>
              <div className="sp-row">
                <div className="sp-row-icon">
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 3h3l1 4-2 1c1 3 3 5 6 6l1-2 4 1v3a2 2 0 01-2 2C7 18 2 13 2 5a2 2 0 012-2z" /></svg>
                </div>
                <div>
                  <div className="sp-label">Phone Support</div>
                  <div>
                    <a href="tel:+18773558587" className="sp-strong">+1 (877) 355-8587</a>
                    <span className="sp-meta">For urgent issues and priority support</span>
                  </div>
                </div>
              </div>
              <div className="sp-row">
                <div className="sp-row-icon">
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="7" /><path d="M10 6v4l3 2" /></svg>
                </div>
                <div>
                  <div className="sp-label">Support Hours</div>
                  <div>
                    <span className="sp-strong">Mon — Fri · 9 AM – 6 PM EST</span>
                    <span className="sp-meta">Emergency support available 24/7</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="sp-card">
              <h3>Popular Support Topics</h3>
              {topics.map((t) => (
                <div key={t.num} className="sp-topic">
                  <div className="sp-topic-bar" />
                  <div>
                    <h4><span className="sp-topic-num">{t.num}</span>{t.title}</h4>
                    <p>{t.desc}</p>
                  </div>
                </div>
              ))}
              <div className="sp-tip">
                <div className="sp-tip-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 1a4 4 0 00-2.5 7.1V11a1 1 0 001 1h3a1 1 0 001-1V8.1A4 4 0 008 1zM6.5 14h3" /></svg>
                </div>
                <div>
                  <h4>QUICK TIP</h4>
                  <p>Before contacting support, try the Knowledge Base — most common questions are answered there with step-by-step guides.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PERFORMANCE BAND */}
      <section className="sp-perf">
        <div className="lp-container">
          <div className="sp-perf-head">
            <span className="lp-eyebrow">SUPPORT PERFORMANCE</span>
            <h2>Numbers we're proud of.</h2>
            <p>We're committed to exceptional support — and these are the numbers that prove it.</p>
          </div>
          <div className="sp-perf-grid">
            {perf.map((p) => (
              <div key={p.l} className="sp-perf-cell">
                <div className="sp-v">{p.v}</div>
                <div className="sp-l">{p.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="lp-cta-banner">
        <div className="lp-cta-card">
          <div>
            <h2 className="lp-h2 lp-cta-h2">Still stuck?<br />Get a real human.</h2>
            <p className="lp-cta-p">One email away from a coaching-savvy support specialist. We don't believe in chatbots.</p>
          </div>
          <a href="mailto:support@lvlup-app.com" className="lp-btn lp-btn-primary lp-btn-lg">
            Email Support
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 7h8m-3-3l3 3-3 3" /></svg>
          </a>
        </div>
      </div>

      </main>

      <SiteFooter />
    </div>
  );
}
